import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calendar,
  User,
  ArrowRight,
  Tag,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = ["All", "TVET", "Enterprise", "Empowerment", "Leadership", "Technology"];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/blog`);
        const data = await res.json();
        setPosts(data);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === "All" 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-slate-50" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              News & Updates
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Blog & Insights
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Stay updated with the latest news, stories, and insights from our programs and communities across Africa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="blog-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="h-5 w-5 text-slate-500 mr-2" weight="duotone" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  selectedCategory === category
                    ? "bg-green-700 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 md:py-28" data-testid="blog-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-slate-200" />
                  <div className="p-6 space-y-3">
                    <div className="flex gap-2">
                      <div className="h-6 w-16 bg-slate-200 rounded-full" />
                      <div className="h-6 w-20 bg-slate-200 rounded-full" />
                    </div>
                    <div className="h-5 w-3/4 bg-slate-200 rounded" />
                    <div className="h-4 w-full bg-slate-100 rounded" />
                    <div className="h-4 w-2/3 bg-slate-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-600 mb-4">No posts found in this category.</p>
              <Button 
                onClick={() => setSelectedCategory("All")}
                variant="outline"
                className="rounded-xl"
              >
                View All Posts
              </Button>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {filteredPosts.length > 0 && (
                <motion.article
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-16"
                  data-testid="featured-post"
                >
                  <div className="grid lg:grid-cols-2 gap-8 items-center bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                    <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden">
                      <img 
                        src={filteredPosts[0].image_url || "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png"} 
                        alt={filteredPosts[0].title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-8 lg:p-10">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          {filteredPosts[0].category}
                        </span>
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          Featured
                        </span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 hover:text-green-700 transition-colors">
                        {filteredPosts[0].title}
                      </h2>
                      <p className="text-slate-600 leading-relaxed mb-6">
                        {filteredPosts[0].excerpt}
                      </p>
                      <div className="flex items-center gap-6 text-sm text-slate-500 mb-6">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" weight="duotone" />
                          <span>{filteredPosts[0].author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" weight="duotone" />
                          <span>{formatDate(filteredPosts[0].created_at)}</span>
                        </div>
                      </div>
                      <Button 
                        className="bg-green-700 hover:bg-green-800 rounded-xl px-6"
                        data-testid="featured-read-more-button"
                      >
                        Read More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.article>
              )}

              {/* Rest of Posts */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(1).map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                    data-testid={`blog-card-${post.id}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={post.image_url || "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0tfpxi02_PP.png"} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3 line-clamp-2 group-hover:text-green-700 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" weight="duotone" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" weight="duotone" />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="blog-newsletter">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Stay Connected
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Subscribe to our newsletter to receive the latest updates on our programs, success stories, and community impact directly in your inbox.
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-green-700 hover:bg-green-800 rounded-xl px-8"
            data-testid="blog-contact-button"
          >
            <Link to="/contact">
              Contact Us <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
